/**
 * WorkbookImporter — generic "upload a spreadsheet, map columns, fix rows, import" flow.
 *
 *   1. Drop / choose a CSV or Excel file (parsed in the browser, nothing uploaded yet).
 *   2. Pick the worksheet and header row; columns are auto-mapped to the schema's fields and can be
 *      remapped from the band under the column letters.
 *   3. Edit any cell in the grid. Client-side validation (required, format, duplicates) runs on every
 *      keystroke; the host's server check (existing records, opt-outs…) runs debounced.
 *   4. Import the corrected rows.
 *
 * The component knows nothing about contacts: the host passes a schema, an optional server validator
 * and the import action. The contact importer is one host; anything tabular can be another.
 */
import { AlertTriangleIcon, CheckCircle2Icon, FileSpreadsheetIcon, Loader2Icon, UploadIcon, XCircleIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { buildRowMarks, detectHeaderRow, detectMapping, headerLabels, validateSheet, type ColumnMap, type ImportSchema, type ServerRowVerdict } from './import-schema';
import { ACCEPT_SPREADSHEETS, fmtBytes, isSpreadsheetName, parseSpreadsheet } from './sheet-io';
import { cellKey, dataColCount, type CellIssue, type SheetDoc } from './sheet-model';
import type { SheetGridHandle } from './SheetGrid';
import { WorkbookViewer } from './WorkbookViewer';

export interface ImportPayload {
  fileName: string;
  sheetName?: string;
  headers: string[];
  rows: string[][];
  /** field key → header label */
  mapping: Record<string, string>;
}

export interface ServerValidation {
  rows: ServerRowVerdict[];
  summary: Array<{ label: string; value: number; tone?: 'default' | 'success' | 'muted' | 'warning' | 'danger' | 'secondary' }>;
  /** Extra detail the host wants to show under the chips. */
  note?: ReactNode;
}

export interface WorkbookImporterProps {
  schema: ImportSchema;
  /** Debounced dry-run against the server. Throwing surfaces the message. */
  validateOnServer?: (payload: ImportPayload, signal: AbortSignal) => Promise<ServerValidation>;
  onImport: (payload: ImportPayload) => Promise<void>;
  importLabel?: (counts: { rows: number; errors: number }) => string;
  /** Rendered in the options column (left) under the file card. */
  options?: ReactNode;
  /** Rendered next to the file picker (template downloads etc.). */
  pickerExtras?: ReactNode;
  /** Bump to re-run the server validation when host options change (e.g. "update existing"). */
  validationKey?: unknown;
  className?: string;
}

type Stage = 'pick' | 'review';

export function WorkbookImporter({ schema, validateOnServer, onImport, importLabel, options, pickerExtras, validationKey, className }: WorkbookImporterProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const gridRef = useRef<SheetGridHandle | null>(null);
  const [stage, setStage] = useState<Stage>('pick');
  const [file, setFile] = useState<{ name: string; size: number } | null>(null);
  const [original, setOriginal] = useState<SheetDoc | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [truncated, setTruncated] = useState<{ sheet: string; keptRows: number }[]>([]);
  const [headerRows, setHeaderRows] = useState<Record<string, number>>({}); // sheet id → header row index in original
  const [doc, setDoc] = useState<SheetDoc | null>(null); // working copy (header at row 0)
  const [mapping, setMapping] = useState<ColumnMap>({});
  const [onlyProblems, setOnlyProblems] = useState(false);
  const [server, setServer] = useState<ServerValidation | null>(null);
  const [serverBusy, setServerBusy] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  // ── load ──────────────────────────────────────────────────────────────────
  const load = useCallback(
    async (f: File) => {
      if (!isSpreadsheetName(f.name)) {
        setParseError('Choose a CSV or Excel file (.csv, .xlsx).');
        return;
      }
      setParseError(null);
      try {
        const bytes = await f.arrayBuffer();
        const { doc: parsed, truncated: t } = parseSpreadsheet(f.name, bytes, 'Import');
        const hr: Record<string, number> = {};
        for (const s of parsed.sheets) hr[s.id] = detectHeaderRow(schema, s.rows);
        setFile({ name: f.name, size: f.size });
        setOriginal(parsed);
        setTruncated(t);
        setHeaderRows(hr);
        setServer(null);
        setStage('review');
      } catch (e) {
        setParseError(e instanceof Error ? e.message : 'Could not read this file.');
      }
    },
    [schema],
  );

  // Working doc: every sheet trimmed so its header is row 0 (frozen). Rebuilt when the header row changes.
  useEffect(() => {
    if (!original) {
      setDoc(null);
      return;
    }
    setDoc((prev) => {
      const sheets = original.sheets.map((s) => {
        const hr = headerRows[s.id] ?? 0;
        const existing = prev?.sheets.find((p) => p.id === s.id);
        // Keep edits when only the active sheet changed; rebuild when the header row moved.
        if (existing && (existing as { __hr?: number }).__hr === hr) return existing;
        const trimmed = { ...s, rows: s.rows.slice(hr), frozenHeader: true, filters: {} };
        Object.defineProperty(trimmed, '__hr', { value: hr, enumerable: false });
        return trimmed;
      });
      return { ...original, sheets, activeSheet: prev?.activeSheet ?? 0, dirty: prev?.dirty ?? false };
    });
  }, [original, headerRows]);

  const activeIndex = doc ? Math.min(doc.activeSheet, doc.sheets.length - 1) : 0;
  const sheet = doc?.sheets[activeIndex] ?? null;
  const width = sheet ? dataColCount(sheet) : 0;
  const headers = useMemo(() => (sheet ? headerLabels(sheet.rows[0] ?? [], width) : []), [sheet, width]);

  // Auto-map when the sheet or its header changes.
  const lastMappedKey = useRef<string>('');
  useEffect(() => {
    if (!sheet) return;
    const key = `${sheet.id}:${headers.join('|')}`;
    if (lastMappedKey.current === key) return;
    lastMappedKey.current = key;
    setMapping(detectMapping(schema, headers));
  }, [sheet, headers, schema]);

  // ── client validation ─────────────────────────────────────────────────────
  const client = useMemo(() => (sheet ? validateSheet(schema, sheet.rows, mapping, { headerRows: 1 }) : null), [sheet, mapping, schema]);

  const payload = useMemo<ImportPayload | null>(() => {
    if (!sheet || !file || !doc) return null;
    const rows = sheet.rows.slice(1).map((r) => Array.from({ length: width }, (_, c) => (r[c] ?? '').toString()));
    const map: Record<string, string> = {};
    for (const [field, col] of Object.entries(mapping)) if (headers[col] !== undefined) map[field] = headers[col] as string;
    return { fileName: file.name, sheetName: doc.sheets.length > 1 ? sheet.name : undefined, headers, rows, mapping: map };
  }, [sheet, file, doc, width, mapping, headers]);

  // ── server validation (debounced) ─────────────────────────────────────────
  const missingRequired = client?.missingRequired ?? [];
  useEffect(() => {
    if (!validateOnServer || !payload || missingRequired.length > 0) {
      setServer(null);
      return;
    }
    const ctrl = new AbortController();
    setServerBusy(true);
    setServerError(null);
    const t = setTimeout(() => {
      validateOnServer(payload, ctrl.signal)
        .then((r) => {
          if (!ctrl.signal.aborted) setServer(r);
        })
        .catch((e: unknown) => {
          if (ctrl.signal.aborted) return;
          setServer(null);
          setServerError(e instanceof Error ? e.message : 'Validation failed');
        })
        .finally(() => {
          if (!ctrl.signal.aborted) setServerBusy(false);
        });
    }, 500);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload, validateOnServer, missingRequired.length, validationKey]);

  // ── decorations ───────────────────────────────────────────────────────────
  const decorations = useMemo(() => {
    if (!client) return undefined;
    const cells = new Map<string, CellIssue>(client.cells);
    if (server) {
      for (const s of server.rows) {
        if (s.field && (s.status === 'error' || s.status === 'skip')) {
          const col = mapping[s.field];
          if (col !== undefined) {
            const k = cellKey(s.index + 1, col);
            if (!cells.has(k)) cells.set(k, { level: s.status === 'error' ? 'error' : 'warning', message: s.reason ?? s.status });
          }
        }
      }
    }
    const rows = buildRowMarks(client, server?.rows ?? null, 1, (status) => (status === 'create' ? 'New' : status === 'update' ? 'Update' : status === 'skip' ? 'Skip' : 'Error'));
    return { cells, rows };
  }, [client, server, mapping]);

  const problemRows = useMemo(() => {
    const set = new Set<number>();
    if (!client) return set;
    for (const v of client.verdicts) if (v.errors.length || v.warnings.length) set.add(v.row);
    for (const s of server?.rows ?? []) if (s.status === 'error' || s.status === 'skip') set.add(s.index + 1);
    return set;
  }, [client, server]);

  const rowPredicate = useMemo(() => (onlyProblems ? (dr: number) => problemRows.has(dr) : undefined), [onlyProblems, problemRows]);

  // ── column band: field mapping ─────────────────────────────────────────────
  const fieldFor = (col: number): string | undefined => Object.entries(mapping).find(([, c]) => c === col)?.[0];
  const setField = (col: number, field: string) => {
    setMapping((m) => {
      const next: ColumnMap = { ...m };
      for (const [k, c] of Object.entries(next)) if (c === col) delete next[k];
      if (field !== '') {
        next[field] = col;
      }
      return next;
    });
  };

  const columnBand = useMemo(
    () => ({
      height: 30,
      render: (col: number) => {
        if (col >= width) return null;
        const current = fieldFor(col) ?? '';
        const spec = schema.fields.find((f) => f.key === current);
        return (
          <select
            value={current}
            onChange={(e) => setField(col, e.target.value)}
            aria-label={`Map column ${headers[col] ?? col + 1}`}
            className={cn(
              'h-full w-full cursor-pointer border-0 bg-transparent px-1.5 text-[11px] outline-none focus:ring-1 focus:ring-primary',
              current ? (spec?.required ? 'font-semibold text-primary' : 'text-foreground') : 'text-muted-foreground italic',
            )}
          >
            <option value="">— skip —</option>
            {schema.fields.map((f) => (
              <option key={f.key} value={f.key} disabled={mapping[f.key] !== undefined && mapping[f.key] !== col}>
                {f.label}
                {f.required ? ' *' : ''}
              </option>
            ))}
          </select>
        );
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mapping, schema, width, headers],
  );

  // ── import ────────────────────────────────────────────────────────────────
  const dataRowCount = payload ? payload.rows.filter((r) => r.some((c) => c.trim() !== '')).length : 0;
  const errorCount = (client?.errorRows ?? 0) + (server?.rows.filter((s) => s.status === 'error').length ?? 0);
  const canImport = !!payload && missingRequired.length === 0 && dataRowCount > 0 && !importing && !serverBusy && !serverError;

  const doImport = async () => {
    if (!payload) return;
    setImporting(true);
    try {
      await onImport(payload);
    } finally {
      setImporting(false);
    }
  };

  const jumpToFirstProblem = () => {
    const first = [...problemRows].sort((a, b) => a - b)[0];
    if (first !== undefined) gridRef.current?.scrollToRow(first);
  };

  // ── render ────────────────────────────────────────────────────────────────
  if (stage === 'pick' || !doc || !sheet) {
    return (
      <div className={cn('grid gap-5', className)}>
        <div
          role="button"
          tabIndex={0}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click();
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            const f = e.dataTransfer.files?.[0];
            if (f) void load(f);
          }}
          className={cn('hover:bg-accent/40 flex flex-col items-center justify-center gap-2 border border-dashed px-6 py-12 text-center transition-colors', dragOver && 'border-primary bg-accent/40')}
        >
          <UploadIcon className="text-muted-foreground size-6" />
          <span className="text-sm font-medium">Drop a spreadsheet here, or click to choose</span>
          <span className="text-muted-foreground text-xs">Excel workbooks (.xlsx) and CSV files. Columns like Name, Phone, Company, Language, Tags are detected automatically.</span>
          {parseError && <span className="text-destructive text-xs">{parseError}</span>}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT_SPREADSHEETS}
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void load(f);
            e.target.value = '';
          }}
        />
        {pickerExtras && <div className="flex flex-wrap items-center justify-between gap-2">{pickerExtras}</div>}
      </div>
    );
  }

  const originalSheet = original?.sheets[activeIndex];
  const headerChoices = (originalSheet?.rows ?? []).slice(0, 10);
  const currentHeaderRow = originalSheet ? (headerRows[originalSheet.id] ?? 0) : 0;
  const trunc = truncated.find((t) => t.sheet === sheet.name);

  return (
    <div className={cn('grid min-h-0 flex-1 gap-4 overflow-y-auto lg:grid-cols-[260px_minmax(0,1fr)] lg:grid-rows-[minmax(0,1fr)] lg:overflow-hidden', className)}>
      <aside className="grid content-start gap-4 lg:min-h-0 lg:overflow-y-auto lg:pr-1">
        <div className="border bg-card p-3">
          <div className="flex items-start gap-2">
            <FileSpreadsheetIcon className="text-muted-foreground mt-0.5 size-4 shrink-0" />
            <div className="min-w-0">
              <div className="truncate text-sm font-medium" title={file?.name}>
                {file?.name}
              </div>
              <div className="text-muted-foreground text-xs">
                {file ? fmtBytes(file.size) : ''} · {dataRowCount.toLocaleString()} rows
                {trunc ? ` (first ${trunc.keptRows.toLocaleString()} shown)` : ''}
              </div>
            </div>
          </div>
          <div className="mt-3 grid gap-2">
            <label className="grid gap-1 text-xs">
              <span className="text-muted-foreground">Header row</span>
              <select
                value={currentHeaderRow}
                onChange={(e) => originalSheet && setHeaderRows((h) => ({ ...h, [originalSheet.id]: Number(e.target.value) }))}
                className="border-input bg-background h-8 w-full border px-2 text-xs"
              >
                {headerChoices.map((row, i) => (
                  <option key={i} value={i}>
                    Row {i + 1}: {row.filter(Boolean).slice(0, 4).join(', ').slice(0, 40) || '(blank)'}
                  </option>
                ))}
              </select>
            </label>
            <Button type="button" variant="ghost" size="sm" className="justify-start" onClick={() => inputRef.current?.click()}>
              <UploadIcon /> Choose a different file
            </Button>
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPT_SPREADSHEETS}
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void load(f);
                e.target.value = '';
              }}
            />
          </div>
        </div>

        <div className="border bg-card p-3">
          <h4 className="text-xs font-semibold tracking-wide uppercase text-muted-foreground">Columns</h4>
          <ul className="mt-2 grid gap-1 text-xs">
            {schema.fields.map((f) => {
              const col = mapping[f.key];
              const mapped = col !== undefined;
              return (
                <li key={f.key} className="flex items-center justify-between gap-2">
                  <span className={cn(f.required && !mapped && 'text-destructive')}>
                    {f.label}
                    {f.required && ' *'}
                  </span>
                  <span className={cn('truncate text-right', mapped ? 'text-foreground' : 'text-muted-foreground italic')}>{mapped ? headers[col] : 'not mapped'}</span>
                </li>
              );
            })}
          </ul>
          <p className="text-muted-foreground mt-2 text-[11px]">Change a mapping from the row under the column letters. Edit any cell to fix a value before importing.</p>
        </div>

        {options}
      </aside>

      <div className="flex min-h-0 min-w-0 flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          {missingRequired.length > 0 ? (
            <Badge variant="danger">
              <XCircleIcon /> Map {missingRequired.join(', ')} to continue
            </Badge>
          ) : (
            <>
              <Badge variant="outline">{dataRowCount.toLocaleString()} rows</Badge>
              {client && client.errorRows > 0 && (
                <button type="button" onClick={jumpToFirstProblem} className="contents">
                  <Badge variant="danger">
                    <XCircleIcon /> {client.errorRows} need fixing
                  </Badge>
                </button>
              )}
              {client && client.warningRows > 0 && (
                <Badge variant="warning">
                  <AlertTriangleIcon /> {client.warningRows} duplicate{client.warningRows === 1 ? '' : 's'} in file
                </Badge>
              )}
              {server?.summary.map((s) => (
                <Badge key={s.label} variant={s.tone ?? 'secondary'}>
                  {s.value.toLocaleString()} {s.label}
                </Badge>
              ))}
              {serverBusy && (
                <span className="text-muted-foreground flex items-center gap-1">
                  <Loader2Icon className="size-3 animate-spin" /> Checking against existing {schema.noun}s…
                </span>
              )}
              {!serverBusy && server && !serverError && (
                <span className="text-success flex items-center gap-1">
                  <CheckCircle2Icon className="size-3" /> Checked
                </span>
              )}
              {serverError && <span className="text-destructive">{serverError}</span>}
            </>
          )}
          <label className="ml-auto flex cursor-pointer items-center gap-2 text-xs">
            <Checkbox checked={onlyProblems} onCheckedChange={(v) => setOnlyProblems(v === true)} disabled={problemRows.size === 0} />
            Only rows with problems ({problemRows.size})
          </label>
        </div>

        <div className="min-h-[280px] flex-1 lg:min-h-0">
          <WorkbookViewer
            doc={doc}
            onDocChange={setDoc}
            editable
            decorations={decorations}
            columnBand={columnBand}
            rowPredicate={rowPredicate}
            gridRef={gridRef}
            showDownload
            status={<span>{doc.dirty ? 'Edited — changes apply to this import only, not the file on disk.' : 'Double-click a cell to edit. Right-click for sort, filters and more.'}</span>}
            className="h-full"
          />
        </div>

        {server?.note}

        <div className="bg-background sticky bottom-0 flex shrink-0 flex-wrap items-center justify-end gap-3 border-t py-3">
          {errorCount > 0 && <span className="text-muted-foreground text-xs">{errorCount.toLocaleString()} row(s) with errors will be skipped.</span>}
          <Button type="button" disabled={!canImport} onClick={() => void doImport()}>
            {importing ? (
              <>
                <Loader2Icon className="animate-spin" /> Importing…
              </>
            ) : (
              (importLabel?.({ rows: dataRowCount, errors: errorCount }) ?? `Import ${(dataRowCount - errorCount).toLocaleString()} rows`)
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
